import _ from 'lodash';
import ExpenseClassEnum from '../Components/Expense/ExpenseClass.enum';
import { ExpenseStatusValuesEnum } from '../Components/Expense/ExpenseStatus.enum';
import moment from 'moment';
import { getExpenseById } from '../Services/ExpenseService';

export const createExpenseFromExpenseEntries = (expenseEntries, isFromVaultPayment) => {
    const expenses = expenseEntries.map((expense) => {
        return prepareExpense(expense);
    });
    return transformToExpenseAPI(expenses, isFromVaultPayment);
};

export const prepareExpense = (originalExpense) => {
    // Do not modify the existing expense object, clone it
    const expense = JSON.parse(JSON.stringify(originalExpense));
    expense.bookingId = parseInt(expense.bookingId, 10);
    expense.clientId = expense.clientId !== '' ? expense.clientId : null;
    expense.mileage = expense.mileage !== '' ? expense.mileage : null;
    expense.vendorId = expense.paymentVendor?.id ? expense.paymentVendor.id : expense.vendorId;
    expense.vendorPayToOrderOf = undefined;

    // Preserve paymentAddress object before converting empty strings to null
    const paymentAddressBackup = expense.paymentAddress;

    Object.keys(expense).forEach((i) => {
        if (i === 'paymentAddress') return; // Skip paymentAddress field
        if (_.isString(expense[i]) && expense[i].length === 0) {
            expense[i] = null;
        }
    });

    // Restore paymentAddress correctly
    if (paymentAddressBackup && typeof paymentAddressBackup === 'object') {
        expense.paymentAddress = paymentAddressBackup;
    } else if (!paymentAddressBackup || paymentAddressBackup === '') {
        expense.paymentAddress = null;
    }

    return expense;
};

export const transformToExpenseAPI = (expenses, isFromVaultPayment) => {
    if (isFromVaultPayment && isFromVaultPayment === true) {
        return expenses[0];
    }
    const rtnExpense = expenses[0];
    rtnExpense.splitExpenses = [];
    rtnExpense.expenseImages = [];
    // Copy to splitExpenses
    for (let i = 1; i < expenses.length; i++) {
        expenses[i].expenseImages = [];
        rtnExpense.splitExpenses.push(expenses[i]);
    }

    return rtnExpense;
};

export const setStatusForEntries = (entries, status) => {
    entries.forEach((entry) => {
        entry.status = status;
    });
    return entries;
};

export const createCorrectionExpenseFromEntries = (entries) => {
    entries = removeAdditionalObjectsFromCorrectionEntries(entries);
    return entries;
};

const removeAdditionalObjectsFromCorrectionEntries = (entries) => {
    entries.forEach((entry) => {
        entry.errors = [];
        entry.relatedErrors = [];
        entry.statusHistory = [];
    });
    return entries;
};

export const isExpenseViewOnlyByStatus = (expense) => {
    if (!expense) return false;

    if (expense.status === ExpenseStatusValuesEnum.Invoiced) {
        return true;
    }

    if (expense.status === ExpenseStatusValuesEnum.Outdated) {
        return true;
    }

    return (
        expense.expenseClass !== ExpenseClassEnum.Vault &&
        (expense.status === ExpenseStatusValuesEnum.Approved ||
            expense.status === ExpenseStatusValuesEnum.Posted)
    );
};

export const isVaultExpenseViewOnlyByStatus = (expense) =>
    expense &&
    expense.expenseClass === ExpenseClassEnum.Vault &&
    (expense.status === ExpenseStatusValuesEnum.Approved ||
        expense.status === ExpenseStatusValuesEnum.Abandoned ||
        expense.status === ExpenseStatusValuesEnum.Invoiced ||
        expense.status === ExpenseStatusValuesEnum.Outdated);

export const validateAllPostingDatesOnSubmit = (expenseEntries, setError, clearErrors) => {
    let allValid = true;

    const today = moment().startOf('day');
    const sixtyDaysAgo = moment().subtract(60, 'days').startOf('day');
    const unifiedMsg = 'Enter a date within the past 60 days.';

    expenseEntries?.forEach((expenseEntry, index) => {
        const fieldPath = `expenseEntries[${index}].postingDate`;
        const postingDateValue = expenseEntry?.postingDate;
        const uiEmpty = expenseEntry?.postingDateUiEmpty === true;

        clearErrors(fieldPath);

        if (uiEmpty) return;
        if (postingDateValue == null || postingDateValue === '') return;

        const normalized = moment(postingDateValue)?.format('MM/DD/YYYY');

        const parsedDate = moment(normalized, 'MM/DD/YYYY', true)?.startOf('day');

        if (
            !parsedDate.isValid() ||
            parsedDate.isAfter(today) ||
            parsedDate.isBefore(sixtyDaysAgo)
        ) {
            setError(fieldPath, { type: 'manual', message: unifiedMsg });
            allValid = false;
        }
    });

    return allValid;
};

export const shouldEnableByCorrectionAndApprovedStatus = (currentExpense) => {
    return (
        currentExpense?.previousVersionID &&
        currentExpense?.status !== ExpenseStatusValuesEnum.Approved &&
        currentExpense?.status !== ExpenseStatusValuesEnum.Posted &&
        currentExpense?.status !== ExpenseStatusValuesEnum.Invoiced
    );
};

export function isInCorrectionToApproved(originalStatus, updatedStatus) {
    return (
        originalStatus === ExpenseStatusValuesEnum.InCorrection &&
        updatedStatus === ExpenseStatusValuesEnum.Approved
    );
}

export async function handleSplitExpenseDeltas(
    expenseResponse,
    originalExpenseEntries,
    createExpenseDelta
) {
    const isAnyCorrection =
        originalExpenseEntries.length > 0 &&
        originalExpenseEntries.some(
            (entry) => entry.status === ExpenseStatusValuesEnum.InCorrection
        );
    if (!isAnyCorrection) return;

    const originalSplitMap = new Map(originalExpenseEntries.map((e) => [e.id, e]));

    const originalPrimaryExpense = originalSplitMap.get(expenseResponse.id);
    if (
        originalPrimaryExpense &&
        isInCorrectionToApproved(originalPrimaryExpense.status, expenseResponse.status)
    ) {
        await createExpenseDelta(expenseResponse.id);
    }

    if (expenseResponse.splitExpenses?.length > 0) {
        for (const split of expenseResponse.splitExpenses) {
            const originalSplit = originalSplitMap.get(split.id);
            if (originalSplit && isInCorrectionToApproved(originalSplit.status, split.status)) {
                await createExpenseDelta(split.id);
            }
        }
    }
}
